import React from 'react';
import CodeBlock from '../../CodeBlock';
import { BookOpen, CheckCircle, Check, AlertTriangle, Lightbulb, Timer, CheckCircle2 } from 'lucide-react';

const GettingStarted = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  return (
    <div className="workflow-section">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4 text-white">Generate Your First SOP</h1>
        <p className="text-lg md:text-xl text-gray-300 leading-7 max-w-[65ch]">
          This guide walks you through generating your first Standard Operating Procedure from documents.
          You'll create an account, upload documents, and generate a structured SOP—all in about 10 minutes.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="card bg-purple-900/20 border border-purple-700/50">
          <h3 className="text-lg font-semibold mb-3 text-purple-400 flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            What you'll build
          </h3>
          <p className="text-sm text-gray-300 mb-4">
            By the end of this guide, you'll have:
          </p>
          <ul className="space-y-2 text-sm text-gray-300">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
              <span>A registered account with API credentials</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
              <span>Documents ingested and processed in a namespace</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
              <span>Your first generated SOP with structured steps</span>
            </li>
          </ul>
        </div>

        <div className="card bg-gray-900/50 border border-gray-800">
          <h3 className="text-lg font-semibold mb-3 text-gray-300 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Before you begin
          </h3>
          <ul className="space-y-2 text-sm text-gray-400">
            <li>• Access to the API server</li>
            <li>• A valid email address</li>
            <li>• An HTTP client (curl, Postman, or similar)</li>
            <li>• At least one document to upload (PDF, DOCX, or TXT)</li>
          </ul>
        </div>
      </div>

      <div className="space-y-6">
        {/* Step 1: Create Account */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">1</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Create your account</h3>
              <p className="text-sm text-gray-400 leading-6">
                Start by creating an account. This returns your API key, which you'll use to authenticate all subsequent requests.
              </p>
            </div>
          </div>

          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request</h4>
              <CodeBlock code={`curl -X POST ${BASE_URL}/users/ \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "your-email@example.com"
  }'`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response</h4>
              <CodeBlock code={`{
  "email": "your-email@example.com",
  "uuid": "4d9804ff-fd89-4c70-9ef6-68fbdf109f6d",
  "api_key": "BEKn4V_JpFrociauurlJCPwjf_X3ui5vGJYOETo1eaA"
}`} />
            </div>

            <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
              <p className="text-sm text-yellow-200 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="font-semibold">Important:</strong> Save your API key securely. The format is <code className="bg-yellow-900/50 px-1 py-0.5 rounded">email:api_key</code>.
                  You cannot retrieve this key again after creation. If you lose it, you'll need to create a new account.
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Step 2: Create Namespace */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">2</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Create a namespace</h3>
              <p className="text-sm text-gray-400 leading-6">
                Namespaces organize your documents and enable document processing. Think of them as containers for related documents that you'll use to generate SOPs.
              </p>
            </div>
          </div>

          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request</h4>
              <CodeBlock code={`curl -X POST ${BASE_URL}/namespaces/ \\
  -H "X-API-Key: your-email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "customer-support-docs",
    "description": "Documents for customer support SOPs"
  }'`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response</h4>
              <CodeBlock code={`{
  "uuid": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  "name": "customer-support-docs",
  "description": "Documents for customer support SOPs",
  "data": {}
}`} />
            </div>

            <p className="text-sm text-gray-400">
              <strong className="text-gray-300">Save the namespace UUID</strong>—you'll need it for document uploads and SOP generation.
            </p>
          </div>
        </div>

        {/* Step 3: Upload Document */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">3</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Upload and process a document</h3>
              <p className="text-sm text-gray-400 leading-6">
                Upload a document to your namespace. The API processes it asynchronously: chunking, embedding, and preparing it for SOP generation.
              </p>
            </div>
          </div>

          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request</h4>
              <CodeBlock code={`curl -X POST ${BASE_URL}/namespaces/{namespace_uuid}/documents \\
  -H "X-API-Key: your-email@example.com:your_api_key_here" \\
  -F "file=@support-handbook.pdf"`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response</h4>
              <CodeBlock code={`{
  "document_uuid": "b2c3d4e5-f6a7-8901-bcde-f12345678901",
  "ingestion_task_uuid": "c3d4e5f6-a7b8-9012-cdef-123456789012",
  "status": "PROCESSING"
}`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-3">Check ingestion status</h4>
              <p className="text-sm text-gray-400 mb-2">
                Poll the ingestion task to know when your document is ready. The status will change from <code className="text-blue-400 bg-gray-800 px-1 py-0.5 rounded">PROCESSING</code> to <code className="text-green-400 bg-gray-800 px-1 py-0.5 rounded">COMPLETED</code>.
              </p>
              <CodeBlock code={`curl -X GET ${BASE_URL}/ingestionTasks/{ingestion_task_uuid} \\
  -H "X-API-Key: your-email@example.com:your_api_key_here"`} />
            </div>

            <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
              <p className="text-sm text-blue-200 flex items-start gap-2">
                <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="font-semibold">Tip:</strong> Document processing typically takes 30 seconds to 2 minutes depending on file size.
                  Wait for <code className="bg-blue-900/50 px-1 py-0.5 rounded">status: "COMPLETED"</code> before proceeding.
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Step 4: Create Project */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">4</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Create a project</h3>
              <p className="text-sm text-gray-400 leading-6">
                Projects organize your SOPs and related resources. Create a project to group related SOPs together.
              </p>
            </div>
          </div>

          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request</h4>
              <CodeBlock code={`curl -X POST ${BASE_URL}/projects/ \\
  -H "X-API-Key: your-email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Customer Support SOPs"
  }'`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response</h4>
              <CodeBlock code={`{
  "uuid": "d4e5f6a7-b8c9-0123-def4-234567890123",
  "name": "Customer Support SOPs",
  "data": {}
}`} />
            </div>
          </div>
        </div>

        {/* Step 5: Generate SOP */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">5</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Generate your SOP</h3>
              <p className="text-sm text-gray-400 leading-6">
                Create an SOP generation task. Provide a clear prompt describing what you want to generate. The API uses your namespace documents as the source material.
              </p>
            </div>
          </div>

          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request</h4>
              <CodeBlock code={`curl -X POST ${BASE_URL}/projects/{project_uuid}/sopGenerationTasks \\
  -H "X-API-Key: your-email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "source_namespace_uuid": "{namespace_uuid}",
    "prompt": "Generate a standard operating procedure for handling customer support inquiries. Include steps for ticket triage, escalation, and resolution."
  }'`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response</h4>
              <CodeBlock code={`{
  "uuid": "e5f6a7b8-c9d0-1234-ef01-345678901234",
  "project_uuid": "{project_uuid}",
  "source_namespace_uuid": "{namespace_uuid}",
  "status": "PROCESSING",
  "prompt": "Generate a standard operating procedure...",
  "created_at": "2024-01-15T10:30:00Z"
}`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-3">Monitor generation progress</h4>
              <p className="text-sm text-gray-400 mb-2">
                SOP generation is asynchronous. Poll the task endpoint to check status. When complete, the response includes an <code className="text-purple-400 bg-gray-800 px-1 py-0.5 rounded">output_sop_uuid</code>.
              </p>
              <CodeBlock code={`curl -X GET ${BASE_URL}/projects/{project_uuid}/sopGenerationTasks/{task_uuid} \\
  -H "X-API-Key: your-email@example.com:your_api_key_here"`} />
            </div>

            <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
              <p className="text-sm text-blue-200 flex items-start gap-2">
                <Timer className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="font-semibold">Processing time:</strong> SOP generation typically takes 2-5 minutes.
                  The status will change from <code className="bg-blue-900/50 px-1 py-0.5 rounded">PROCESSING</code> to <code className="bg-green-900/50 px-1 py-0.5 rounded">COMPLETED</code> when ready.
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Step 6: Retrieve SOP */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">6</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Retrieve your SOP</h3>
              <p className="text-sm text-gray-400 leading-6">
                Once generation completes, retrieve the generated SOP using the <code className="text-purple-400 bg-gray-800 px-1 py-0.5 rounded">output_sop_uuid</code> from the task response.
              </p>
            </div>
          </div>

          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request</h4>
              <CodeBlock code={`curl -X GET ${BASE_URL}/sops/{sop_uuid} \\
  -H "X-API-Key: your-email@example.com:your_api_key_here"`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response</h4>
              <CodeBlock code={`{
  "uuid": "{sop_uuid}",
  "title": "Standard Operating Procedure: Customer Support Inquiry Handling",
  "content": "This SOP outlines the process for handling customer support inquiries...",
  "steps": [
    {
      "step_number": 1,
      "title": "Receive and Log Inquiry",
      "description": "When a customer inquiry is received...",
      "detailed_steps": ["Check ticket system", "Assign priority level", "Log in database"]
    },
    {
      "step_number": 2,
      "title": "Triage and Categorize",
      "description": "Evaluate the inquiry type and urgency...",
      "detailed_steps": ["Identify inquiry type", "Assess urgency", "Route to appropriate team"]
    }
  ],
  "created_at": "2024-01-15T10:35:00Z"
}`} />
            </div>

            <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg">
              <p className="text-sm text-green-200 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="font-semibold">Success!</strong> You've successfully generated your first SOP.
                  The response includes the complete SOP with all steps, detailed procedures, and metadata.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="card mt-8 bg-gray-900 border border-gray-800">
        <h3 className="text-lg font-bold tracking-tight text-gray-200 mb-3">What's next?</h3>
        <div className="space-y-3 text-sm text-gray-300">
          <p className="leading-6">
            Now that you've generated your first SOP, here are some next steps:
          </p>
          <ul className="space-y-2 list-disc list-inside ml-2">
            <li>Upload more documents to your namespace to enhance SOP quality</li>
            <li>Generate additional SOPs with different prompts</li>
            <li>Explore the <code className="text-blue-400 bg-gray-800 px-1 py-0.5 rounded">/sop-resources</code> endpoint to view all your generated SOPs</li>
            <li>Review and iterate on your prompts to refine the output</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default GettingStarted;
